import { isValidLongPathSegment } from 'src/utils/route-param-id';
import { buildMultipleKeyForArrayParams } from 'src/utils/query-params-utils';

import { BasicListParams } from 'src/types/common';
import {
  Room,
  Lesson,
  Mission,
  Feedback,
  MyLearning,
  Progression,
  MyClassDegree,
  ResMissionUpdate,
  ResMissionSubmit,
  ReqMyLearningList,
  ReqSaveMissionFeedback,
} from 'src/types/learn';

import axiosInstance from './instance';

type LearnerLessonRoomParams = MyLearning & Lesson & Room;

const validateLearnerProgression = (methodName: string, params: MyLearning & Progression) => {
  const { learnerId, progressionId } = params;
  if (!learnerId || progressionId === undefined || progressionId === null || progressionId === '') {
    const msg = `[learnApi.${methodName}] missing learnerId or progressionId`;
    console.warn(msg, params);
    return Promise.reject(new Error(msg));
  }
  if (!isValidLongPathSegment(learnerId) || !isValidLongPathSegment(progressionId)) {
    const msg = `[learnApi.${methodName}] invalid learnerId or progressionId format`;
    console.warn(msg, params);
    return Promise.reject(new Error(msg));
  }
  return null;
};

const validateLearnerId = (methodName: string, learnerId: string | undefined) => {
  if (!isValidLongPathSegment(learnerId)) {
    const msg = `[learnApi.${methodName}] invalid or missing learnerId`;
    console.warn(msg, learnerId);
    return Promise.reject(new Error(msg));
  }
  return null;
};

const validateLearnerLesson = (methodName: string, params: MyLearning & Lesson) => {
  const invalidLearner = validateLearnerId(methodName, params.learnerId);
  if (invalidLearner) return invalidLearner;
  if (!isValidLongPathSegment(params.lessonId)) {
    const msg = `[learnApi.${methodName}] invalid lessonId format`;
    console.warn(msg, params);
    return Promise.reject(new Error(msg));
  }
  return null;
};

const validateLearnerLessonRoom = (methodName: string, params: LearnerLessonRoomParams) => {
  const invalid = validateLearnerLesson(methodName, params);
  if (invalid) return invalid;
  if (!isValidLongPathSegment(params.roomId)) {
    const msg = `[learnApi.${methodName}] invalid roomId format`;
    console.warn(msg, params);
    return Promise.reject(new Error(msg));
  }
  return null;
};

const validateLearnerMission = (methodName: string, params: MyLearning & Mission) => {
  const invalidLearner = validateLearnerId(methodName, params.learnerId);
  if (invalidLearner) return invalidLearner;
  if (!isValidLongPathSegment(params.assignmentId)) {
    const msg = `[learnApi.${methodName}] invalid assignmentId format`;
    console.warn(msg, params);
    return Promise.reject(new Error(msg));
  }
  return null;
};

/**
 * Learner API
 */
const learnApi = {
  // 나의 학습목록(get learning list)
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_my_course.html#resources-degree-learner-fetch
  fetchList(params: ReqMyLearningList) {
    const { categories, ...rest } = params;
    const builtCategory = categories?.length
      ? buildMultipleKeyForArrayParams('categories', categories)
      : '';
    return axiosInstance.get(`/learn/learner/online?${builtCategory}`, { params: rest });
  },

  // 나의 학습 달성률(get learning progress rate)
  // https://dev.clp.kr/anchor/api/static/docs/online_learner.html#resources-degree-learner-progressrate-detail
  learnProgressRate(params: MyLearning) {
    const invalid = validateLearnerId('learnProgressRate', params.learnerId);
    if (invalid) return invalid;
    return axiosInstance.get(`/learn/learner/online/${params.learnerId}/progressRate/detail`);
  },

  // 학습 차시 컨텐츠(get lesson contents)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_learner_learn.html#resources-learner-degree-lesson-contents
  learnLessonContents(params: MyLearning & Progression) {
    const invalid = validateLearnerProgression('learnLessonContents', params);
    if (invalid) return invalid;
    return axiosInstance.get(
      `/learn/learner/online/${params.learnerId}/lesson/${params.progressionId}/contents`
    );
  },
  // 나의 학습 목록 카운트(My Recent Learning List Count)
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_my_course.html#resources-degree-learner-count
  learnCountLesson(params: { status: string; name: string }) {
    return axiosInstance.get(`/learn/learner/online/count`, { params });
  },
  // 학습 - 차시 진도율 업데이트(update lesson progression)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_learner_learn.html#resources-learner-increment-progression
  learnLessonProgression(params: MyLearning & Progression) {
    const invalid = validateLearnerProgression('learnLessonProgression', params);
    if (invalid) return invalid;
    return axiosInstance.put(
      `/learn/learner/online/${params.learnerId}/lesson/${params.progressionId}/progression`,
      params
    );
  },

  // 학습목표 API 호출(get Learning Objectives)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_learner_learn.html#resources-online-learner-degree-lesson-intro
  learnLessonObjective(params: MyLearning & Progression) {
    const invalid = validateLearnerProgression('learnLessonObjective', params);
    if (invalid) return invalid;
    return axiosInstance.get(
      `/learn/learner/online/${params.learnerId}/lesson/${params.progressionId}/intro`
    );
  },

  // 나의 학습 상세 기본정보(get learning basic information)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_learner_my_course.html#resources-degree-learner-detail
  learnViewBasic(params: MyLearning) {
    const invalid = validateLearnerId('learnViewBasic', params.learnerId);
    if (invalid) return invalid;
    return axiosInstance.get(`/learn/learner/online/${params.learnerId}`);
  },

  // 학습 - 차시 추가 자료 or 퀴즈(get lesson Additional Materials or Quiz)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_learner_learn.html#resources-learner-lesson-timelinecontents
  learnLessonTimelinecontents(params: MyLearning & Progression) {
    const invalid = validateLearnerProgression('learnLessonTimelinecontents', params);
    if (invalid) return invalid;
    return axiosInstance.get(
      `/learn/learner/online/${params.learnerId}/lesson/${params.progressionId}/timelinecontents`
    );
  },

  // 좋아요 취소(cancel lesson like)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_learner_learn.html#resources-learner-lesson-prog-likes-remove
  learnLessonLikesDelete(params: MyLearning & Progression) {
    const invalid = validateLearnerProgression('learnLessonLikesDelete', params);
    if (invalid) return invalid;
    return axiosInstance.delete(
      `/learn/learner/online/${params.learnerId}/lesson/${params.progressionId}/likes`
    );
  },

  // 좋아요(set lesson like)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_learner_learn.html#resources-learner-lesson-prog-likes-register
  learnLessonLikesPost(params: MyLearning & Progression) {
    const invalid = validateLearnerProgression('learnLessonLikesPost', params);
    if (invalid) return invalid;
    return axiosInstance.post(
      `/learn/learner/online/${params.learnerId}/lesson/${params.progressionId}/likes`
    );
  },

  // 북마크(set lesson bookmark)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_learner_learn.html#resources-learner-lesson-prog-clipping-register
  learnLessonClippingPost(params: MyLearning & Progression) {
    const invalid = validateLearnerProgression('learnLessonClippingPost', params);
    if (invalid) return invalid;
    return axiosInstance.post(
      `/learn/learner/online/${params.learnerId}/lesson/${params.progressionId}/clipping`
    );
  },

  // 북마크 취소(cancel lesson bookmark)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_learner_learn.html#resources-learner-lesson-prog-clipping-remove
  learnLessonClippingDelete(params: MyLearning & Progression) {
    const invalid = validateLearnerProgression('learnLessonClippingDelete', params);
    if (invalid) return invalid;
    return axiosInstance.delete(
      `/learn/learner/online/${params.learnerId}/lesson/${params.progressionId}/clipping`
    );
  },

  // 나의 학습 상세 레슨 목록(get learning courses list)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_learner_my_course.html#resources-degree-learner-resource-courses
  learnViewCourses(params: MyLearning) {
    const invalid = validateLearnerId('learnViewCourses', params.learnerId);
    if (invalid) return invalid;
    return axiosInstance.get(`/learn/learner/online/${params.learnerId}/courses`);
  },

  // 나의 학습 시작하기(set learning start)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_learner_learn.html#resources-learner-degree-start
  learnStart(params: MyLearning) {
    const invalid = validateLearnerId('learnStart', params.learnerId);
    if (invalid) return invalid;
    return axiosInstance.post(`/learn/learner/online/${params.learnerId}/start`);
  },

  // 나의 학습 상세 - 다음 학습(get next lesson)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_learner_my_course.html#resources-degree-learner-resource-lesson-next
  learnLessonNext(params: MyLearning) {
    const invalid = validateLearnerId('learnLessonNext', params.learnerId);
    if (invalid) return invalid;
    return axiosInstance.get(`/learn/learner/online/${params.learnerId}/lesson/next`);
  },

  // 나의 학습 상세 시험 목록(get learning exams list)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_learner_my_course.html#resources-degree-learner-resource-exams
  learnViewExams(params: MyLearning) {
    const invalid = validateLearnerId('learnViewExams', params.learnerId);
    if (invalid) return invalid;
    return axiosInstance.get(`/learn/learner/online/${params.learnerId}/exams`);
  },

  // 나의 학습 상세 미션 목록(get learning missions list)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_learner_my_course.html#resources-degree-learner-mission-list
  learnViewMission(params: MyLearning) {
    const invalid = validateLearnerId('learnViewMission', params.learnerId);
    if (invalid) return invalid;
    return axiosInstance.get(`/learn/learner/online/${params.learnerId}/mission`);
  },

  // 나의 학습 상세 미션 새소식 알림(get learning missions notice information)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_learner_my_course.html#resources-degree-learner-mission-notify-exists
  learnViewMissionNotice(params: MyLearning) {
    const invalid = validateLearnerId('learnViewMissionNotice', params.learnerId);
    if (invalid) return invalid;
    return axiosInstance.get(`/learn/learner/online/${params.learnerId}/mission/exists/notify`);
  },

  // 나의 학습 채팅방 목록(get lesson question chatting room list)
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_chat.html#resources-online-learner-degree-chatroom-page
  learnChatroomList(params: MyLearning) {
    const invalid = validateLearnerId('learnChatroomList', params.learnerId);
    if (invalid) return invalid;
    return axiosInstance.get(`/learn/learner/online/${params.learnerId}/chatroom?sort=id,ASC`);
  },

  // 나의 학습 상세 설문 목록(get learning survey list)
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_my_course.html#resources-degree-learner-survey-list
  learnViewSurveys(params: MyLearning) {
    const invalid = validateLearnerId('learnViewSurveys', params.learnerId);
    if (invalid) return invalid;
    return axiosInstance.get(`/learn/learner/online/${params.learnerId}/surveys`);
  },

  // 차시 채팅방 정보(Get lesson chat information)
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_chat.html#resources-online-learner-lesson-chatroom-page
  learnChatroomLesson(params: MyLearning & Lesson) {
    const invalid = validateLearnerLesson('learnChatroomLesson', params);
    if (invalid) return invalid;
    return axiosInstance.get(
      `/learn/learner/online/${params.learnerId}/lesson/${params.lessonId}/chatroom`
    );
  },

  learnCreateChatroomLesson({ degreeId, lessonId }: MyClassDegree & Lesson) {
    return axiosInstance.post(`/learn/online/${degreeId}/lesson/${lessonId}/chatroom/room`);
  },

  // 차시 채팅방 대화 목록(Get lesson chatting room list)
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_chat.html#resources-online-learner-lesson-chats-page
  learnChatroomChatList(params: LearnerLessonRoomParams) {
    const invalid = validateLearnerLessonRoom('learnChatroomChatList', params);
    if (invalid) return invalid;
    return axiosInstance.get(
      `/learn/learner/online/${params.learnerId}/lesson/${params.lessonId}/chatroom/${params.roomId}/chats`,
      { params }
    );
  },

  // 문항 채팅 읽음 처리(Set lesson chat readed
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_chat.html#resources-online-learner-lesson-chats-read
  learnChatroomChatRead(params: LearnerLessonRoomParams) {
    const invalid = validateLearnerLessonRoom('learnChatroomChatRead', params);
    if (invalid) return invalid;
    return axiosInstance.put(
      `/learn/learner/online/${params.learnerId}/lesson/${params.lessonId}/chatroom/${params.roomId}/read`
    );
  },

  // 차시 채팅방 읽음 처리(Set lesson chat readed)
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_chat.html#resources-online-learner-lesson-chats-write
  learnChatroomChat(params: MyLearning & Lesson) {
    const invalid = validateLearnerLesson('learnChatroomChat', params);
    if (invalid) return invalid;
    return axiosInstance.post(
      `/learn/learner/online/${params.learnerId}/lesson/${params.lessonId}/chatroom/chat`,
      params
    );
  },

  // 나의 학습 상세 미션 상세(get learning mission detail)
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_my_course.html#resources-degree-learner-mission-detail
  learnViewMissionView(params: MyLearning & Mission) {
    const invalid = validateLearnerMission('learnViewMissionView', params);
    if (invalid) return invalid;
    return axiosInstance.get(
      `/learn/learner/online/${params.learnerId}/mission/${params.assignmentId}`
    );
  },

  // 북마크(set Mission bookmark)
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_learn.html#resources-learner-mission-clipping
  learnMissionClippingPost(params: MyLearning & Mission) {
    const invalid = validateLearnerMission('learnMissionClippingPost', params);
    if (invalid) return invalid;
    return axiosInstance.post(
      `/learn/learner/online/${params.learnerId}/mission/${params.assignmentId}/clipping`
    );
  },

  // 북마크 취소(cancel Mission bookmark)
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_learn.html#resources-learner-mission-unClipping
  learnMissionClippingDelete(params: MyLearning & Mission) {
    const invalid = validateLearnerMission('learnMissionClippingDelete', params);
    if (invalid) return invalid;
    return axiosInstance.delete(
      `/learn/learner/online/${params.learnerId}/mission/${params.assignmentId}/unClipping`
    );
  },

  // 나의 학습 상세 미션 피드백 목록(get learning mission feedback list)
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_my_course.html#resources-degree-learner-mission-feedback-list
  learnViewMissionFeedback(params: MyLearning & Mission) {
    const invalid = validateLearnerMission('learnViewMissionFeedback', params);
    if (invalid) return invalid;
    return axiosInstance.get(
      `/learn/learner/online/${params.learnerId}/mission/${params.assignmentId}/feedback`,
      { params }
    );
  },

  // 나의 학습 상세 미션 피드백 등록(regist learning mission feedback)
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_my_course.html#resources-degree-learner-mission-feedback-register
  learnViewMissionFeedbackSubmit(params: MyLearning & Mission & ReqSaveMissionFeedback) {
    const invalid = validateLearnerMission('learnViewMissionFeedbackSubmit', params);
    if (invalid) return invalid;
    return axiosInstance.post(
      `/learn/learner/online/${params.learnerId}/mission/${params.assignmentId}/feedback`,
      { ...params }
    );
  },

  // 나의 학습 상세 미션 피드백 삭제(delete learning mission feedback)
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_my_course.html#resources-degree-learner-mission-feedback-delete
  learnViewMissionFeedbackDelete(params: MyLearning & Mission & Feedback) {
    const invalid = validateLearnerMission('learnViewMissionFeedbackDelete', params);
    if (invalid) return invalid;
    if (!isValidLongPathSegment(params.feedbackId)) {
      const msg = '[learnApi.learnViewMissionFeedbackDelete] invalid feedbackId format';
      console.warn(msg, params);
      return Promise.reject(new Error(msg));
    }
    return axiosInstance.delete(
      `/learn/learner/online/${params.learnerId}/mission/${params.assignmentId}/feedback/${params.feedbackId}`
    );
  },

  // 나의 학습 상세 미션 제출(submit learning mission)
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_my_course.html#resources-degree-learner-mission-register
  learnViewMissionSubmit(params: ResMissionSubmit) {
    if (
      !isValidLongPathSegment(params.learnId) ||
      !isValidLongPathSegment(params.assignmentId)
    ) {
      const msg = '[learnApi.learnViewMissionSubmit] invalid learnId or assignmentId format';
      console.warn(msg, params);
      return Promise.reject(new Error(msg));
    }
    return axiosInstance.post(
      `/learn/learner/online/${params.learnId}/mission/${params.assignmentId}`,
      { ...params }
    );
  },

  // 나의 학습 상세 미션 수정(update learning mission)
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_my_course.html#resources-degree-learner-mission-modify
  learnViewMissionUpdate(params: ResMissionUpdate) {
    if (
      !isValidLongPathSegment(params.learnId) ||
      !isValidLongPathSegment(params.assignmentId) ||
      !isValidLongPathSegment(params.answerId)
    ) {
      const msg = '[learnApi.learnViewMissionUpdate] invalid path id format';
      console.warn(msg, params);
      return Promise.reject(new Error(msg));
    }
    return axiosInstance.put(
      `/learn/learner/online/${params.learnId}/mission/${params.assignmentId}/${params.answerId}`,
      { ...params }
    );
  },

  // 리뷰 등록
  // https://dev.clp.kr/anchor/api/static/docs/front/online_degree_detail.html#resources-online-degree-review-register
  learnPostReview(params: BasicListParams & { degreeId: string; content: string; score: number }) {
    return axiosInstance.post(`/learn/online/${params.degreeId}/review`, params);
  },

  // 포인트(Get degree point information)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_degree_detail.html#resources-online-degree-detail-point
  guestLearnOnlineViewPoint(params: { degreeId: string }) {
    return axiosInstance.get(`/learn/online/${params.degreeId}/policy/point`);
  },

  // 리뷰 수정 (Review modify)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_degree_detail.html#resources-online-degree-review-modify
  learnEditReview(params: { id: string; degreeId: string; content: string; score: number }) {
    return axiosInstance.put(`/learn/online/${params.degreeId}/review`, params);
  },

  // 리뷰 삭제 (Review remove)
  // https://dev.clp.kr/anchor/api/static/docs/front/online_degree_detail.html#resources-online-degree-review-remove
  learnDeleteReview(params: { degreeId: string; reviewId: string }) {
    return axiosInstance.delete(`/learn/online/${params.degreeId}/review/${params.reviewId}`);
  },

  // 과제 히스토리 조회
  // https://dev.clp.kr/api/static/docs/online_learner_my_course.html#resources-front-mission-history
  learnMissionHistory(id: string) {
    if (!isValidLongPathSegment(id)) {
      const msg = '[learnApi.learnMissionHistory] invalid id format';
      console.warn(msg, id);
      return Promise.reject(new Error(msg));
    }
    return axiosInstance.get(`/mission/answer/${id}/history`);
  },

  // 과정 오픈 (Next Learning Open)
  // https://dev.clp.kr/spc/api/static/docs/front/online_learner_my_course.html#resources-degree-learner-resource-course-open
  openNextLearning(id: string) {
    const invalid = validateLearnerId('openNextLearning', id);
    if (invalid) return invalid;
    return axiosInstance.put(`/learn/learner/online/${id}/course/open`);
  },

  // 온라인 학습 카테고리 (Online Learning Category)
  // https://dev.clp.kr/spc/api/static/docs/front/online_degree_category.html
  fetchDegreeCategoryList() {
    return axiosInstance.get(`/learn/degree/category`);
  },

  // 채팅 방 없을 시 채팅 방 생성(Create chat room)
  createChatroom(params: { degreeId: string | number; lessonId: string | number }) {
    return axiosInstance.post(
      `/learn/online/${params.degreeId}/lesson/${params.lessonId}/chatroom/room`
    );
  },

  // 딥링크의 온라인 학습 상세 id 조회 (Online Learning Detail by Deeplink)
  // https://api-dev.clp.kr/anchor/static/docs/front/online_learner_my_course.html#resources-online-learner-fetch-by-degree-id
  getOnlineLearnDetail(id: string) {
    if (!isValidLongPathSegment(id)) {
      const msg = '[learnApi.getOnlineLearnDetail] invalid id format';
      console.warn(msg, id);
      return Promise.reject(new Error(msg));
    }
    return axiosInstance.get(`/learn/learner/online/by-degree/${id}`);
  },
};

export default learnApi;
